import { router } from "@inertiajs/react";
import { AlertTriangle, DollarSign, MessageSquare } from "lucide-react";
import { useMemo, useState } from "react";

import CurrencyFormatHint from "@/components/currency-format-hint";
import { SupplierObservationsDialog } from "@/components/supplier-observations-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { formatCurrencyWithSymbol } from "@/helpers/currency-formatter";
import type { PriceComparison, SelectionsTotals } from "@/types";

interface Props {
  comparison: PriceComparison;
  selectionsTotals?: SelectionsTotals | null;
}

const formatCurrency = (amount: number, currency = "ARS") =>
  new Intl.NumberFormat("es-AR", { style: "currency", currency }).format(amount);

export default function SelectedItemsTable({ comparison, selectionsTotals }: Props) {
  const [observationsDialog, setObservationsDialog] = useState<{
    supplierName: string;
    quoteId: number;
    buyerNotes?: string | null;
  } | null>(null);

  const itemsBySupplier = useMemo(() => {
    const map = new Map<number, {
      supplierName: string;
      quoteId?: number;
      buyerNotes?: string | null;
      paymentMethod?: string | null;
      paymentTermDays?: string | null;
      rows: { description: string; quantity: number; unit: string; priceArs?: number | null; isBest: boolean }[]
    }>();

    comparison.rows.forEach((row) => {
      const selectedPrice = row.prices.find((p) => p?.is_selected);
      if (!selectedPrice?.supplier_id) return;
      const supplierId = selectedPrice.supplier_id;
      const supplierName = selectedPrice.supplier_name ?? `Proveedor ${supplierId}`;

      // Find supplier info with quote_id and buyer_notes
      const supplierInfo = comparison.headers.suppliers.find((s) => s.id === supplierId);

      const entry = map.get(supplierId) ?? {
        supplierName,
        quoteId: supplierInfo?.quote_id,
        buyerNotes: supplierInfo?.buyer_notes,
        paymentMethod: supplierInfo?.payment_method ?? null,
        paymentTermDays: supplierInfo?.payment_term_days ?? null,
        rows: []
      };
      entry.rows.push({
        description: row.request_item.description,
        quantity: row.request_item.quantity,
        unit: row.request_item.unit,
        priceArs: selectedPrice.unit_price_ars,
        isBest: !!selectedPrice.is_best_price,
      });
      map.set(supplierId, entry);
    });
    return Array.from(map.entries()).map(([supplierId, v]) => ({ supplierId, ...v }));
  }, [comparison.rows, comparison.headers.suppliers]);

  const totalsFromBackend = selectionsTotals ?? {
    subtotal_ars: 0,
    taxable_subtotal_ars: 0,
    tax_amount_ars: 0,
    total_ars: 0
  };

  if (itemsBySupplier.length === 0) return null;

  return (
    <Card variant="outline">
      <CardHeader>
        <CardTitle>Productos seleccionados</CardTitle>
        <CardDescription>
          Para modificar o cambiar los productos, selecciónalos en la tabla superior y haz clic en Guardar.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="mb-4">
          <CurrencyFormatHint />
        </div>
        {itemsBySupplier.map((group) => (
          <div key={group.supplierId} className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <div className="font-medium flex flex-col">
                <span>{group.supplierName}</span>
              </div>
              {group.quoteId && (
                <Button
                  size="sm"
                  variant="outline"
                  className="ml-auto"
                  onClick={() => setObservationsDialog({
                    supplierName: group.supplierName,
                    quoteId: group.quoteId!,
                    buyerNotes: group.buyerNotes,
                  })}
                  title="Agregar observaciones"
                >
                  <MessageSquare className="h-4 w-4 mr-1" />
                  Observaciones
                </Button>
              )}
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Artículo</TableHead>
                    <TableHead className="text-center">Cantidad</TableHead>
                    <TableHead className="text-center">Unidad</TableHead>
                    <TableHead className="text-center">Precio (ARS)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {group.rows.map((r, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="whitespace-normal break-words">{r.description}</TableCell>
                      <TableCell className="text-center">{r.quantity}</TableCell>
                      <TableCell className="text-center">{r.unit}</TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          {r.priceArs != null ? formatCurrency(r.priceArs) : "-"}
                          {!r.isBest && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="inline-flex">
                                  <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>No es el mejor precio</TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Forma de pago debajo del total/tabla */}
            {(group.paymentMethod ?? group.paymentTermDays) && (
              <div className="mt-2 text-sm text-slate-700">
                <strong>Forma de pago:</strong>{" "}
                {[
                  group.paymentMethod ?? undefined,
                  group.paymentTermDays ? `${group.paymentTermDays} días` : undefined,
                ].filter(Boolean).join(" - ")}
              </div>
            )}
          </div>
        ))}

        {/* Total General Section */}
        {itemsBySupplier.length > 0 && (
          <>
            <Separator className="my-6" />
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-start gap-2 text-slate-700">
                <DollarSign className="h-5 w-5 text-slate-600 mt-0.5" />
                <span className="text-sm font-semibold">Totales con IVA</span>
              </div>
              <div className="mt-3 space-y-2">
                <div className="flex items-center justify-between text-sm text-slate-700">
                  <span>Subtotal</span>
                  <span className="font-medium">{formatCurrencyWithSymbol(totalsFromBackend.subtotal_ars, "ARS")}</span>
                </div>
                <div className="flex items-center justify-between text-sm text-slate-600">
                  <span>IVA</span>
                  <span className="font-medium">{formatCurrencyWithSymbol(totalsFromBackend.tax_amount_ars, "ARS")}</span>
                </div>
                <Separator />
                <div className="flex items-center justify-between text-lg font-bold text-slate-900">
                  <span>Total final</span>
                  <span className="text-2xl sm:text-3xl">{formatCurrencyWithSymbol(totalsFromBackend.total_ars, "ARS")}</span>
                </div>
              </div>
            </div>
          </>
        )}
      </CardContent>

      {observationsDialog && (
        <SupplierObservationsDialog
          open={!!observationsDialog}
          onOpenChange={(open) => !open && setObservationsDialog(null)}
          supplierName={observationsDialog.supplierName}
          quoteId={observationsDialog.quoteId}
          initialNotes={observationsDialog.buyerNotes}
          onSaved={() => router.reload({ only: ["price_comparison"] })}
        />
      )}
    </Card>
  );
}
